import { get, post, put, del } from '../../../common/axiosClient';
import { API } from '../../../common/constants/urls';
import {
  LimitedReportDTO,
  CreateLimitedReportDTO,
  UpdateLimitedReportDTO,
  LimitedReportInvoiceDTO,
  CreateLimitedReportInvoiceDTO,
  UpdateLimitedReportInvoiceDTO,
  LimitedReportPhotoDTO,
  LimitedReportPhotoUploadResponse,
  BatchLimitedReportPhotoUpdateDTO,
} from './limited-report.type';
import { PdfImageQuality } from '../shared/utils/pdfCompression';
import { PdfExportResult, requestPdfExport } from '../shared/utils/pdfExportRequest';

export const createLimitedReport = async (createDto: CreateLimitedReportDTO) => {
  return (await post(`${API.LIMITED_REPORTS}`, createDto)) as LimitedReportDTO;
};

export const getLimitedReport = async (id: string) => {
  return (await get(`${API.LIMITED_REPORTS}/${id}`)) as LimitedReportDTO;
};

export const updateLimitedReport = async (id: string, updateDto: UpdateLimitedReportDTO) => {
  return (await put(`${API.LIMITED_REPORTS}/${id}`, updateDto)) as LimitedReportDTO;
};

export const deleteLimitedReport = async (id: string) => {
  return (await del(`${API.LIMITED_REPORTS}/${id}`)) as string;
};

export const getAllLimitedReports = async () => {
  return (await get(`${API.LIMITED_REPORTS}`)) as LimitedReportDTO[];
};

export const getLimitedReportsByProject = async (projectId: string) => {
  return (await get(`${API.LIMITED_REPORTS}?projectId=${projectId}`)) as LimitedReportDTO[];
};

// Invoice API functions
export const createLimitedReportInvoice = async (createDto: CreateLimitedReportInvoiceDTO) => {
  return (await post(`${API.LIMITED_REPORTS}/invoices`, createDto)) as LimitedReportInvoiceDTO;
};

export const getLimitedReportInvoices = async (limitedReportId: string) => {
  return (await get(`${API.LIMITED_REPORTS}/${limitedReportId}/invoices`)) as LimitedReportInvoiceDTO[];
};

export const getLimitedReportInvoice = async (invoiceId: string) => {
  return (await get(`${API.LIMITED_REPORTS}/invoices/${invoiceId}`)) as LimitedReportInvoiceDTO;
};

export const updateLimitedReportInvoice = async (invoiceId: string, updateDto: UpdateLimitedReportInvoiceDTO) => {
  return (await put(`${API.LIMITED_REPORTS}/invoices/${invoiceId}`, updateDto)) as LimitedReportInvoiceDTO;
};

export const deleteLimitedReportInvoice = async (invoiceId: string) => {
  return (await del(`${API.LIMITED_REPORTS}/invoices/${invoiceId}`)) as string;
};

// New API function for saving/updating invoices
export const saveLimitedReportInvoice = async (limitedReportId: string, createDto: CreateLimitedReportInvoiceDTO) => {
  return (await post(`${API.LIMITED_REPORTS}/${limitedReportId}/invoice`, createDto)) as LimitedReportInvoiceDTO;
};

export const updateLimitedReportCurrentInvoice = async (
  limitedReportId: string,
  invoiceId: string,
  createDto: CreateLimitedReportInvoiceDTO,
) => {
  return (await put(`${API.LIMITED_REPORTS}/${limitedReportId}/invoice/${invoiceId}`, createDto)) as LimitedReportInvoiceDTO;
};

// Photo management API methods
export const uploadPhoto = async (
  limitedReportId: string,
  file: File,
  onUploadProgress?: (progress: number) => void,
  sortOrder?: number,
) => {
  const formData = new FormData();
  formData.append('file', file);

  const url =
    sortOrder !== undefined
      ? `${API.LIMITED_REPORTS}/${limitedReportId}/photos?sortOrder=${sortOrder}`
      : `${API.LIMITED_REPORTS}/${limitedReportId}/photos`;

  const response = (await post(url, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    onUploadProgress: (progressEvent: ProgressEvent) => {
      const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
      onUploadProgress?.(progress);
    },
  })) as LimitedReportPhotoUploadResponse;

  // Map to generic PhotoUploadResponse
  return {
    id: response.photoId,
    blobName: response.pdfBlobName || response.originalBlobName,
    originalBlobName: response.originalBlobName,
    pdfBlobName: response.pdfBlobName,
    thumbnailBlobName: response.thumbnailBlobName,
    fileName: response.fileName,
    fileSize: response.fileSize,
    width: response.width,
    height: response.height,
    sortOrder: response.sortOrder,
    reportId: limitedReportId,
    caption: '',
    hasCaption: false,
  };
};

export const getPhotos = async (limitedReportId: string) => {
  const photos = (await get(`${API.LIMITED_REPORTS}/${limitedReportId}/photos`)) as any[];
  // Map backend response (with limitedReportId) to generic ReportPhotoDTO (with reportId)
  return photos.map(photo => ({
    ...photo,
    blobName: photo.pdfBlobName || photo.blobName || photo.originalBlobName,
    reportId: photo.limitedReportId || limitedReportId,
  })) as LimitedReportPhotoDTO[];
};

export const updatePhotoCaption = async (limitedReportId: string, photoId: string, caption: string) => {
  return (await put(`${API.LIMITED_REPORTS}/${limitedReportId}/photos/${photoId}/caption`, { caption })) as string;
};

export const reorderPhotos = async (limitedReportId: string, newOrder: string[]) => {
  return (await put(`${API.LIMITED_REPORTS}/${limitedReportId}/photos/reorder`, newOrder)) as string;
};

// Signature base64 API function. The timestamp defeats any HTTP cache entry keyed on this URL
// (browser or shared proxy), so a re-uploaded signature can never be shadowed by the previous one.
export const getSignatureAsBase64 = async () => {
  return (await get(`${API.USER}/signature/base64?t=${Date.now()}`)) as { dataUrl: string };
};

export const batchUpdatePhotos = async (limitedReportId: string, dto: BatchLimitedReportPhotoUpdateDTO) => {
  return (await put(`${API.LIMITED_REPORTS}/${limitedReportId}/photos/batch`, dto)) as string;
};

export const deletePhoto = async (limitedReportId: string, photoId: string) => {
  return (await del(`${API.LIMITED_REPORTS}/${limitedReportId}/photos/${photoId}`)) as string;
};

// Export-related API methods
export const getImageAsBase64 = async (blobName: string) => {
  const encodedBlobName = encodeURIComponent(blobName);
  return (await get(`${API.PROJECT}files/${encodedBlobName}/base64`)) as string;
};

export const updateEditedPhoto = async (limitedReportId: string, photoId: string, editedImageBlob: Blob) => {
  const formData = new FormData();
  formData.append('file', editedImageBlob, 'edited-photo.jpg');

  const photo = (await put(`${API.LIMITED_REPORTS}/${limitedReportId}/photos/${photoId}/replace`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  })) as any;

  // Map backend response to generic ReportPhotoDTO
  return {
    ...photo,
    reportId: photo.limitedReportId || limitedReportId,
  } as LimitedReportPhotoDTO;
};

// Playwright-based export endpoint
export const exportLimitedReportPdf = async (html: string, addBorder = true, imageQuality?: PdfImageQuality): Promise<PdfExportResult> =>
  requestPdfExport(`${API.LIMITED_REPORTS}/export`, html, addBorder, imageQuality);
